import { ExpenseRepository } from "./expenseRepository";
import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import { ValidationError } from "./projectRepository";
import type { LoanActivity, LoanActivityInput, LoanRateChange, WorkspaceFinancingHistory98 } from "../../domain/financing";
import type {
  DrawStatus,
  FacilityInputFields,
  FinancingFacility,
  LenderDraw,
  PaymentPlan,
  RentalAssumptions,
  VarianceNote,
} from "../../domain/financing";

/**
 * FINANCING persistence (spec §73C/E/F).
 *
 * Holds what a contract says and what a person decided; every figure the
 * planning screens show is derived by the pure engines. An unknown term is
 * NULL and stays NULL — this repository never fills a blank with zero.
 */

interface FacilityRow {
  id: string;
  project_id: string | null;
  name: string;
  lender: string;
  loan_type: string;
  commitment_cents: number | null;
  acquisition_funding_cents: number | null;
  rehab_facility_cents: number | null;
  down_payment_cents: number | null;
  interest_rate_pct_x100: number | null;
  rate_type: string;
  interest_basis: string;
  interest_only: number;
  origination_points_x100: number | null;
  origination_fee_cents: number | null;
  other_fees_cents: number | null;
  draw_fee_cents: number | null;
  minimum_draw_cents: number | null;
  payment_cadence: string;
  closing_date: string | null;
  first_payment_date: string | null;
  maturity_date: string | null;
  extension_notes: string;
  prepayment_notes: string;
  ltc_pct_x100: number | null;
  ltv_pct_x100: number | null;
  rehab_reimbursement: string;
  rehab_funding_pct_x100: number | null;
  refinance_ltv_pct_x100: number | null;
  refinance_closing_costs_cents: number | null;
  payoff_facility_id: string | null;
  expected_date: string | null;
  notes: string;
  terms_source: string;
  confirmed_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  owner_scope?: string; capital_source_id?: string | null; day_count_convention?: string | null;
  loan_structure?: string | null; interest_only_months?: number | null; amortization_months?: number | null; amortization_start_rule?: string | null;
}

const n = (v: number | null): number | null => (v === null ? null : Number(v));

function mapActivity(r: Record<string, unknown>): LoanActivity {
  return { drawId: r.draw_id as string|null, dueDate: r.due_date as string|null, feeTarget: r.fee_target === "origination" ? "origination" : r.fee_target === "draw" ? "draw" : "other", id: String(r.id), facilityId: String(r.facility_id), date: String(r.date), kind: r.kind as LoanActivity["kind"],
    amountCents: Number(r.amount_cents), principalCents: Number(r.principal_cents), interestCents: Number(r.interest_cents), feeCents: Number(r.fee_cents),
    expenseId: r.expense_id as string | null, reversalOfId: r.reversal_of_id as string | null, notes: String(r.notes), createdAt: String(r.created_at) };
}

function mapFacility(row: FacilityRow): FinancingFacility {
  return {
    id: row.id,
    projectId: row.project_id,
    ownerScope: (row.owner_scope ?? "project") as FinancingFacility["ownerScope"],
    capitalSourceId: row.capital_source_id ?? null,
    dayCountConvention: (row.day_count_convention ?? null) as FinancingFacility["dayCountConvention"],
    loanStructure: (row.loan_structure ?? null) as FinancingFacility["loanStructure"],
    interestOnlyMonths: row.interest_only_months ?? null, amortizationMonths: row.amortization_months ?? null,
    amortizationStartRule: (row.amortization_start_rule ?? null) as FinancingFacility["amortizationStartRule"],
    name: row.name,
    lender: row.lender,
    loanType: row.loan_type as FinancingFacility["loanType"],
    commitmentCents: n(row.commitment_cents),
    acquisitionFundingCents: n(row.acquisition_funding_cents),
    rehabFacilityCents: n(row.rehab_facility_cents),
    downPaymentCents: n(row.down_payment_cents),
    interestRatePctX100: n(row.interest_rate_pct_x100),
    rateType: row.rate_type as FinancingFacility["rateType"],
    interestBasis: row.interest_basis as FinancingFacility["interestBasis"],
    interestOnly: Number(row.interest_only) === 1,
    originationPointsX100: n(row.origination_points_x100),
    originationFeeCents: n(row.origination_fee_cents),
    otherFeesCents: n(row.other_fees_cents),
    drawFeeCents: n(row.draw_fee_cents),
    minimumDrawCents: n(row.minimum_draw_cents),
    paymentCadence: row.payment_cadence as FinancingFacility["paymentCadence"],
    closingDate: row.closing_date,
    firstPaymentDate: row.first_payment_date,
    maturityDate: row.maturity_date,
    extensionNotes: row.extension_notes,
    prepaymentNotes: row.prepayment_notes,
    ltcPctX100: n(row.ltc_pct_x100),
    ltvPctX100: n(row.ltv_pct_x100),
    rehabReimbursement: row.rehab_reimbursement as FinancingFacility["rehabReimbursement"],
    rehabFundingPctX100: n(row.rehab_funding_pct_x100),
    refinanceLtvPctX100: n(row.refinance_ltv_pct_x100),
    refinanceClosingCostsCents: n(row.refinance_closing_costs_cents),
    payoffFacilityId: row.payoff_facility_id,
    expectedDate: row.expected_date,
    notes: row.notes,
    termsSource: row.terms_source as FinancingFacility["termsSource"],
    confirmedAt: row.confirmed_at,
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const FACILITY_COLUMNS: Array<[column: string, value: (f: FacilityInputFields) => unknown]> = [
  ["owner_scope", f => f.ownerScope ?? "project"], ["capital_source_id", f => f.capitalSourceId ?? null],
  ["day_count_convention", f => f.dayCountConvention ?? null], ["loan_structure", f => f.loanStructure ?? null],
  ["interest_only_months", f => f.interestOnlyMonths ?? null], ["amortization_months", f => f.amortizationMonths ?? null],
  ["amortization_start_rule", f => f.amortizationStartRule ?? null],
  ["name", (f) => f.name],
  ["lender", (f) => f.lender],
  ["loan_type", (f) => f.loanType],
  ["commitment_cents", (f) => f.commitmentCents],
  ["acquisition_funding_cents", (f) => f.acquisitionFundingCents],
  ["rehab_facility_cents", (f) => f.rehabFacilityCents],
  ["down_payment_cents", (f) => f.downPaymentCents],
  ["interest_rate_pct_x100", (f) => f.interestRatePctX100],
  ["rate_type", (f) => f.rateType],
  ["interest_basis", (f) => f.interestBasis],
  ["interest_only", (f) => (f.interestOnly ? 1 : 0)],
  ["origination_points_x100", (f) => f.originationPointsX100],
  ["origination_fee_cents", (f) => f.originationFeeCents],
  ["other_fees_cents", (f) => f.otherFeesCents],
  ["draw_fee_cents", (f) => f.drawFeeCents],
  ["minimum_draw_cents", (f) => f.minimumDrawCents],
  ["payment_cadence", (f) => f.paymentCadence],
  ["closing_date", (f) => f.closingDate],
  ["first_payment_date", (f) => f.firstPaymentDate],
  ["maturity_date", (f) => f.maturityDate],
  ["extension_notes", (f) => f.extensionNotes],
  ["prepayment_notes", (f) => f.prepaymentNotes],
  ["ltc_pct_x100", (f) => f.ltcPctX100],
  ["ltv_pct_x100", (f) => f.ltvPctX100],
  ["rehab_reimbursement", (f) => f.rehabReimbursement],
  ["rehab_funding_pct_x100", (f) => f.rehabFundingPctX100],
  ["refinance_ltv_pct_x100", (f) => f.refinanceLtvPctX100],
  ["refinance_closing_costs_cents", (f) => f.refinanceClosingCostsCents],
  ["payoff_facility_id", (f) => f.payoffFacilityId],
  ["expected_date", (f) => f.expectedDate],
  ["notes", (f) => f.notes],
  ["terms_source", (f) => f.termsSource],
];

export const EMPTY_FACILITY: FacilityInputFields = {
  name: "",
  lender: "",
  loanType: "acquisition-rehab",
  commitmentCents: null,
  acquisitionFundingCents: null,
  rehabFacilityCents: null,
  downPaymentCents: null,
  interestRatePctX100: null,
  rateType: "fixed",
  interestBasis: "unknown",
  interestOnly: true,
  originationPointsX100: null,
  originationFeeCents: null,
  otherFeesCents: null,
  drawFeeCents: null,
  minimumDrawCents: null,
  paymentCadence: "unknown",
  closingDate: null,
  firstPaymentDate: null,
  maturityDate: null,
  extensionNotes: "",
  prepaymentNotes: "",
  ltcPctX100: null,
  ltvPctX100: null,
  rehabReimbursement: "unknown",
  rehabFundingPctX100: null,
  refinanceLtvPctX100: null,
  refinanceClosingCostsCents: null,
  payoffFacilityId: null,
  expectedDate: null,
  notes: "",
  termsSource: "manual",
};

interface DrawRow {
  id: string;
  facility_id: string;
  label: string;
  status: string;
  planned_date: string | null;
  funded_date: string | null;
  requested_cents: number | null;
  approved_cents: number | null;
  received_cents: number | null;
  fee_cents: number | null;
  task_id: string | null;
  notes: string;
  sort_order: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapDraw(row: DrawRow): LenderDraw {
  return {
    id: row.id,
    facilityId: row.facility_id,
    label: row.label,
    status: row.status as DrawStatus,
    plannedDate: row.planned_date,
    fundedDate: row.funded_date,
    requestedCents: n(row.requested_cents),
    approvedCents: n(row.approved_cents),
    receivedCents: n(row.received_cents),
    feeCents: n(row.fee_cents),
    taskId: row.task_id,
    notes: row.notes,
    sortOrder: Number(row.sort_order),
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

interface PaymentPlanRow {
  id: string;
  project_id: string;
  label: string;
  direction: string;
  source_kind: string;
  source_id: string | null;
  pct_x100: number | null;
  amount_cents: number | null;
  anchor: string;
  task_id: string | null;
  date: string | null;
  offset_days: number;
  notes: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapPaymentPlan(row: PaymentPlanRow): PaymentPlan {
  return {
    id: row.id,
    projectId: row.project_id,
    label: row.label,
    direction: row.direction as PaymentPlan["direction"],
    sourceKind: row.source_kind as PaymentPlan["sourceKind"],
    sourceId: row.source_id,
    pctX100: n(row.pct_x100),
    amountCents: n(row.amount_cents),
    anchor: row.anchor as PaymentPlan["anchor"],
    taskId: row.task_id,
    date: row.date,
    offsetDays: Number(row.offset_days),
    notes: row.notes,
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

interface RentalRow {
  project_id: string;
  monthly_rent_cents: number | null;
  vacancy_pct_x100: number | null;
  property_tax_annual_cents: number | null;
  insurance_annual_cents: number | null;
  hoa_monthly_cents: number | null;
  management_pct_x100: number | null;
  maintenance_pct_x100: number | null;
  utilities_monthly_cents: number | null;
  other_monthly_cents: number | null;
  lease_up_days: number | null;
  updated_at: string;
}

function mapRental(row: RentalRow): RentalAssumptions {
  return {
    projectId: row.project_id,
    monthlyRentCents: n(row.monthly_rent_cents),
    vacancyPctX100: n(row.vacancy_pct_x100),
    propertyTaxAnnualCents: n(row.property_tax_annual_cents),
    insuranceAnnualCents: n(row.insurance_annual_cents),
    hoaMonthlyCents: n(row.hoa_monthly_cents),
    managementPctX100: n(row.management_pct_x100),
    maintenancePctX100: n(row.maintenance_pct_x100),
    utilitiesMonthlyCents: n(row.utilities_monthly_cents),
    otherMonthlyCents: n(row.other_monthly_cents),
    leaseUpDays: n(row.lease_up_days),
    updatedAt: row.updated_at,
  };
}

export class FinancingRepository {
  constructor(private readonly db: DbExecutor) {}

  /** Owner workspace history in two bounded reads, including project-owned facilities. */
  async workspaceSourceHistory98(from?:string,to?:string):Promise<WorkspaceFinancingHistory98>{
    const limit=5000;
    const [activities,draws]=await Promise.all([
      this.db.select<Record<string,unknown>>("SELECT a.*,f.project_id AS history_project_id FROM loan_activities a JOIN financing_facilities f ON f.id=a.facility_id WHERE (? IS NULL OR a.date>=?) AND (? IS NULL OR a.date<=?) ORDER BY a.date DESC,a.created_at DESC,a.id LIMIT 5001",[from??null,from??null,to??null,to??null]),
      this.db.select<DrawRow&{history_project_id:string|null}>("SELECT d.*,f.project_id AS history_project_id FROM lender_draws d JOIN financing_facilities f ON f.id=d.facility_id WHERE (? IS NULL OR COALESCE(d.funded_date,d.planned_date)>=?) AND (? IS NULL OR COALESCE(d.funded_date,d.planned_date)<=?) ORDER BY COALESCE(d.funded_date,d.planned_date) DESC,d.id LIMIT 5001",[from??null,from??null,to??null,to??null]),
    ]);
    return{activities:activities.slice(0,limit).map(r=>({...mapActivity(r),projectId:r.history_project_id as string|null})),draws:draws.slice(0,limit).map(r=>({...mapDraw(r),projectId:r.history_project_id})),limit,activityHasMore:activities.length>limit,drawHasMore:draws.length>limit};
  }

  /* ---------------- facilities ---------------- */

  async listFacilities(projectId: string | null): Promise<FinancingFacility[]> {
    const rows = await this.db.select<FacilityRow>(
      `SELECT * FROM financing_facilities WHERE ${projectId === null ? "project_id IS NULL" : "project_id = ?"} AND archived_at IS NULL ORDER BY created_at`,
      projectId === null ? [] : [projectId],
    );
    return rows.map(mapFacility);
  }

  async getFacility(id: string): Promise<FinancingFacility | null> {
    const rows = await this.db.select<FacilityRow>("SELECT * FROM financing_facilities WHERE id = ?", [id]);
    return rows.length === 0 ? null : mapFacility(rows[0]);
  }

  async createFacility(projectId: string | null, fields: FacilityInputFields, requestId?:string): Promise<FinancingFacility> {
    fields = { ...fields, ownerScope: projectId === null ? "business" : "project" };
    this.validateTerms(fields);
    if(requestId!==undefined&&!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(requestId))throw new ValidationError(["This loan save identity is invalid. Reopen the form before trying again."]);
    const id = requestId ?? generateId();
    // The caller may retry after losing a successful response. The facility's
    // own UUID is the claim; no parallel ledger, name matching or new table.
    return this.db.transaction(async()=>{
      const existing=await this.getFacility(id);
      if(existing){
        if(existing.projectId!==projectId||existing.archivedAt||FACILITY_COLUMNS.some(([,value])=>value(existing)!==value(fields)))throw new ValidationError(["This loan save was already used for different terms or another source. Open the saved loan to review it instead of creating a duplicate."]);
        return existing;
      }
      const now = new Date().toISOString();
      const columns = FACILITY_COLUMNS.map(([c]) => c);
      await this.db.execute(
        `INSERT INTO financing_facilities (id, project_id, ${columns.join(", ")}, created_at, updated_at)
         VALUES (?, ?, ${columns.map(() => "?").join(", ")}, ?, ?)`,
        [id, projectId, ...FACILITY_COLUMNS.map(([, v]) => v(fields)), now, now],
      );
      return (await this.getFacility(id))!;
    });
  }

  async updateFacility(id: string, fields: FacilityInputFields): Promise<FinancingFacility> {
    const old = await this.getFacility(id);
    if (!old) throw new ValidationError(["That loan no longer exists."]);
    fields = { ...fields, ownerScope: old.projectId === null ? "business" : "project" };
    this.validateTerms(fields);
    const recorded = (await this.db.select<{n:number}>("SELECT (SELECT COUNT(*) FROM loan_activities WHERE facility_id = ?) + (SELECT COUNT(*) FROM lender_draws WHERE facility_id = ? AND status = 'funded') AS n",[id,id]))[0].n;
    if((Number(recorded)>0 || ((old.acquisitionFundingCents??0)>0 && !!old.closingDate && old.closingDate<=new Date().toISOString().slice(0,10))) && (fields.acquisitionFundingCents!==old.acquisitionFundingCents || fields.closingDate!==old.closingDate)) throw new ValidationError(["Recorded funding history is preserved. Use a dated principal correction instead of changing initial funding."]);
    const outstanding = await this.outstandingPrincipal(id);
    if (fields.commitmentCents !== null && fields.commitmentCents < outstanding) throw new ValidationError(["The facility limit cannot be less than funded outstanding principal."]);
    const now = new Date().toISOString();
    await this.db.execute(
      `UPDATE financing_facilities SET ${FACILITY_COLUMNS.map(([c]) => `${c} = ?`).join(", ")}, updated_at = ?
       WHERE id = ?`,
      [...FACILITY_COLUMNS.map(([, v]) => v(fields)), now, id],
    );
    return (await this.getFacility(id))!;
  }

  /** Terms reviewed against the agreement: the facility is CONFIRMED. */
  async confirmFacility(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute("UPDATE financing_facilities SET confirmed_at = ?, updated_at = ? WHERE id = ?", [
      now, now, id,
    ]);
  }

  /** Delete = archive; history is never destroyed. */
  async archiveFacility(id: string): Promise<void> {
    await this.db.transaction(async () => {
      const facility = await this.getFacility(id);
      if (!facility) throw new ValidationError(["That loan no longer exists."]);
      // Active-facility queries feed debt and cash projections. Hiding a loan
      // with money history would hide those facts, even after its balance is zero.
      const history = (await this.db.select<{ n: number }>(
        "SELECT (SELECT COUNT(*) FROM lender_draws WHERE facility_id = ? AND status = 'funded') + (SELECT COUNT(*) FROM loan_activities WHERE facility_id = ?) AS n", [id, id],
      ))[0];
      const initialFundingRecorded = (facility.acquisitionFundingCents ?? 0) > 0 && !!facility.closingDate && facility.closingDate <= new Date().toISOString().slice(0, 10);
      if (initialFundingRecorded || Number(history.n) > 0) {
        throw new ValidationError(["This loan has recorded funding or payment history and cannot be removed. Record a dated repayment, payoff or correction instead; its history must remain in the financial plan."]);
      }
      const now = new Date().toISOString();
      await this.db.execute("UPDATE financing_facilities SET archived_at = ?, updated_at = ? WHERE id = ?", [now, now, id]);
    });
  }

  /* ---------------- loan document linkage ---------------- */

  async attachDocument(facilityId: string, attachmentId: string): Promise<void> {
    await this.db.execute(
      "INSERT OR IGNORE INTO financing_facility_attachments (facility_id, attachment_id) VALUES (?, ?)",
      [facilityId, attachmentId],
    );
  }

  async listDocumentIds(facilityId: string): Promise<string[]> {
    const rows = await this.db.select<{ attachment_id: string }>(
      "SELECT attachment_id FROM financing_facility_attachments WHERE facility_id = ?",
      [facilityId],
    );
    return rows.map((r) => r.attachment_id);
  }

  /** What the interpreter PROPOSED — never authoritative (spec §73D). */
  async recordExtraction(projectId: string, attachmentId: string, proposedJson: string): Promise<string> {
    const id = generateId();
    await this.db.execute(
      "INSERT INTO financing_extractions (id, project_id, attachment_id, proposed_json, created_at) VALUES (?, ?, ?, ?, ?)",
      [id, projectId, attachmentId, proposedJson, new Date().toISOString()],
    );
    return id;
  }

  async getExtraction(attachmentId: string): Promise<{ id: string; proposedJson: string } | null> {
    const rows = await this.db.select<{ id: string; proposed_json: string }>(
      "SELECT id, proposed_json FROM financing_extractions WHERE attachment_id = ? ORDER BY created_at DESC LIMIT 1",
      [attachmentId],
    );
    return rows.length === 0 ? null : { id: rows[0].id, proposedJson: rows[0].proposed_json };
  }

  /* ---------------- draws ---------------- */

  async listDraws(projectId: string | null): Promise<LenderDraw[]> {
    const rows = await this.db.select<DrawRow>(
      `SELECT d.* FROM lender_draws d
        WHERE d.archived_at IS NULL
          AND d.facility_id IN (SELECT id FROM financing_facilities WHERE ${projectId === null ? "project_id IS NULL" : "project_id = ?"})
        ORDER BY d.sort_order, d.created_at`,
      projectId === null ? [] : [projectId],
    );
    return rows.map(mapDraw);
  }

  async createDraw(
    facilityId: string,
    fields: Omit<LenderDraw, "id" | "facilityId" | "archivedAt" | "createdAt" | "updatedAt">,
  ): Promise<LenderDraw> {
    return this.db.transaction(async () => {
    await this.validateDraw(facilityId, fields);
    const id = generateId();
    const now = new Date().toISOString();
    await this.db.execute(
      `INSERT INTO lender_draws (id, facility_id, label, status, planned_date, funded_date, requested_cents,
         approved_cents, received_cents, fee_cents, task_id, notes, sort_order, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id, facilityId, fields.label, fields.status, fields.plannedDate, fields.fundedDate,
        fields.requestedCents, fields.approvedCents, fields.receivedCents, fields.feeCents,
        fields.taskId, fields.notes, fields.sortOrder, now, now,
      ],
    );
    if(fields.status === "funded" && (fields.feeCents??0)>0) await this.recordLoanActivity(facilityId,{date:fields.fundedDate!,kind:"fee-payment",amountCents:fields.feeCents!,feeTarget:"draw",drawId:id,notes:"Fee recorded with funded draw"});
    return (await this.getDraw(id))!;
    });
  }

  async getDraw(id: string): Promise<LenderDraw | null> {
    const rows = await this.db.select<DrawRow>("SELECT * FROM lender_draws WHERE id = ?", [id]);
    return rows.length === 0 ? null : mapDraw(rows[0]);
  }

  async updateDraw(
    id: string,
    fields: Omit<LenderDraw, "id" | "facilityId" | "archivedAt" | "createdAt" | "updatedAt">,
  ): Promise<LenderDraw> {
    return this.db.transaction(async () => {
    const existing = await this.getDraw(id);
    if (!existing) throw new ValidationError(["That draw no longer exists."]);
    if (existing.status === "funded" && (fields.status !== "funded" || fields.fundedDate !== existing.fundedDate || fields.receivedCents !== existing.receivedCents || fields.feeCents !== existing.feeCents)) throw new ValidationError(["Funded draw history is preserved. Record a repayment or correction instead."]);
    await this.validateDraw(existing.facilityId, fields, id);
    await this.db.execute(
      `UPDATE lender_draws SET label = ?, status = ?, planned_date = ?, funded_date = ?, requested_cents = ?,
         approved_cents = ?, received_cents = ?, fee_cents = ?, task_id = ?, notes = ?, sort_order = ?,
         updated_at = ? WHERE id = ?`,
      [
        fields.label, fields.status, fields.plannedDate, fields.fundedDate, fields.requestedCents,
        fields.approvedCents, fields.receivedCents, fields.feeCents, fields.taskId, fields.notes,
        fields.sortOrder, new Date().toISOString(), id,
      ],
    );
    if(existing.status !== "funded" && fields.status === "funded" && (fields.feeCents??0)>0) await this.recordLoanActivity(existing.facilityId,{date:fields.fundedDate!,kind:"fee-payment",amountCents:fields.feeCents!,feeTarget:"draw",drawId:id,notes:"Fee recorded with funded draw"});
    return (await this.getDraw(id))!;
    });
  }

  async archiveDraw(id: string): Promise<void> {
    if ((await this.getDraw(id))?.status === "funded") throw new ValidationError(["A funded draw cannot be removed. Record a repayment or correction."]);
    const now = new Date().toISOString();
    await this.db.execute("UPDATE lender_draws SET archived_at = ?, updated_at = ? WHERE id = ?", [now, now, id]);
  }

  private validateTerms(f: FacilityInputFields): void {
    if(f.dayCountConvention && !["act365","act360","30-360","monthly"].includes(f.dayCountConvention)) throw new ValidationError(["Choose a supported day-count convention."]);
    if(f.loanStructure && !["interest-only","amortizing","io-amortizing","revolving","balloon"].includes(f.loanStructure)) throw new ValidationError(["Choose a supported loan structure."]);
    const amounts = [f.commitmentCents, f.acquisitionFundingCents, f.rehabFacilityCents, f.interestRatePctX100, f.originationFeeCents, f.otherFeesCents, f.drawFeeCents, f.minimumDrawCents, f.interestOnlyMonths, f.amortizationMonths];
    if (amounts.some(n => n !== null && n !== undefined && (!Number.isSafeInteger(n) || n < 0))) throw new ValidationError(["Loan amounts, rates and terms must be nonnegative exact integers."]);
    if (f.commitmentCents !== null && (f.acquisitionFundingCents ?? 0) > f.commitmentCents) throw new ValidationError(["Initial drawn principal exceeds the facility limit."]);
    if (f.amortizationMonths === 0 || (f.amortizationMonths ?? 0) > 1200 || (f.interestOnlyMonths ?? 0) > 1200) throw new ValidationError(["Amortization must be 1–1200 months; IO duration cannot exceed 1200 months."]);
    if (f.loanStructure === "io-amortizing" && f.amortizationStartRule === "includes-io" && (f.amortizationMonths ?? 0) <= (f.interestOnlyMonths ?? 0)) throw new ValidationError(["Amortization including IO must extend beyond the IO period."]);
    for (const date of [f.closingDate, f.firstPaymentDate, f.maturityDate]) if (date) this.validDate(date);
    if (f.closingDate && f.firstPaymentDate && f.firstPaymentDate < f.closingDate) throw new ValidationError(["First due date precedes origination."]);
    if (f.closingDate && f.maturityDate && f.maturityDate < f.closingDate) throw new ValidationError(["Maturity precedes origination."]);
  }

  private validDate(date: string): void {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(Date.parse(`${date}T00:00:00Z`)) || new Date(`${date}T00:00:00Z`).toISOString().slice(0,10) !== date) throw new ValidationError(["Enter a valid calendar date."]);
  }

  async outstandingPrincipal(facilityId: string, throughDate = "9999-12-31"): Promise<number> {
    const f = await this.getFacility(facilityId); if (!f) throw new ValidationError(["That loan no longer exists."]);
    const draws = await this.db.select<{ n: number }>("SELECT COALESCE(SUM(received_cents),0) AS n FROM lender_draws WHERE facility_id = ? AND status = 'funded' AND archived_at IS NULL AND funded_date <= ?", [facilityId, throughDate]);
    const paid = await this.db.select<{ n: number }>("SELECT COALESCE(SUM(principal_cents),0) AS n FROM loan_activities WHERE facility_id = ? AND date <= ?", [facilityId, throughDate]);
    return (f.closingDate && f.closingDate <= throughDate ? f.acquisitionFundingCents ?? 0 : 0) + Number(draws[0].n) - Number(paid[0].n);
  }

  private async validateDraw(facilityId: string, fields: Omit<LenderDraw,"id"|"facilityId"|"archivedAt"|"createdAt"|"updatedAt">, excludeId = ""): Promise<void> {
    const f = await this.getFacility(facilityId); if (!f || f.archivedAt) throw new ValidationError(["Choose an active loan."]);
    for (const value of [fields.requestedCents,fields.approvedCents,fields.receivedCents,fields.feeCents]) if (value !== null && (!Number.isSafeInteger(value) || value < 0)) throw new ValidationError(["Draw amounts must be nonnegative integer cents."]);
    for (const date of [fields.plannedDate,fields.fundedDate]) if (date) this.validDate(date);
    if (fields.status === "funded" && (!fields.fundedDate || fields.receivedCents === null)) throw new ValidationError(["A funded draw requires its actual funded date and amount received."]);
    if (fields.status === "funded" && f.commitmentCents !== null) {
      const other = await this.db.select<{ n:number }>("SELECT COALESCE(SUM(received_cents),0) AS n FROM lender_draws WHERE facility_id = ? AND id <> ? AND status = 'funded' AND archived_at IS NULL", [facilityId,excludeId]);
      const repayments = f.loanStructure === "revolving" ? await this.db.select<{ n:number }>("SELECT COALESCE(SUM(principal_cents),0) AS n FROM loan_activities WHERE facility_id = ? AND date <= ?",[facilityId,fields.fundedDate]) : [{n:0}];
      if ((f.acquisitionFundingCents ?? 0) + Number(other[0].n) + (fields.receivedCents ?? 0) - Number(repayments[0].n) > f.commitmentCents) throw new ValidationError(["This funded draw exceeds the facility's available capacity."]);
    }
  }

  async listRateChanges(facilityId: string): Promise<LoanRateChange[]> {
    const rows = await this.db.select<{id:string;facility_id:string;effective_date:string;rate_pct_x100:number;created_at:string}>("SELECT * FROM financing_rate_changes WHERE facility_id = ? ORDER BY effective_date",[facilityId]);
    return rows.map(r=>({id:r.id,facilityId:r.facility_id,date:r.effective_date,ratePctX100:Number(r.rate_pct_x100),createdAt:r.created_at}));
  }

  /** One scoped read regardless of facility count; archived loans stay excluded. */
  async listRateChangesForProject(projectId: string | null): Promise<LoanRateChange[]> {
    const rows = await this.db.select<{id:string;facility_id:string;effective_date:string;rate_pct_x100:number;created_at:string}>(
      `SELECT r.* FROM financing_rate_changes r JOIN financing_facilities f ON f.id = r.facility_id
       WHERE f.archived_at IS NULL AND ${projectId === null ? "f.project_id IS NULL" : "f.project_id = ?"}
       ORDER BY r.facility_id, r.effective_date`, projectId === null ? [] : [projectId]);
    return rows.map(r=>({id:r.id,facilityId:r.facility_id,date:r.effective_date,ratePctX100:Number(r.rate_pct_x100),createdAt:r.created_at}));
  }

  async listAllFacilities(): Promise<FinancingFacility[]> { return (await this.db.select<FacilityRow>("SELECT * FROM financing_facilities WHERE archived_at IS NULL ORDER BY created_at")).map(mapFacility); }

  async listFacilityAllocations(facilityId?: string): Promise<Array<{ id:string;facilityId:string;projectId:string;amountCents:number }>> {
    return (await this.db.select<{id:string;facility_id:string;project_id:string;amount_cents:number}>(`SELECT * FROM financing_facility_allocations${facilityId ? " WHERE facility_id = ?" : ""}`,facilityId?[facilityId]:[])).map(r=>({id:r.id,facilityId:r.facility_id,projectId:r.project_id,amountCents:Number(r.amount_cents)}));
  }

  async setRateChange(facilityId: string, date: string, ratePctX100: number): Promise<void> {
    this.validDate(date);
    if (!Number.isSafeInteger(ratePctX100) || ratePctX100 < 0) throw new ValidationError(["Rate must be a nonnegative exact percentage."]);
    const facility=await this.getFacility(facilityId); if(!facility || facility.archivedAt) throw new ValidationError(["Choose an active loan."]);
    if((await this.listRateChanges(facilityId)).some(r=>r.date===date)) throw new ValidationError(["A rate is already recorded for this effective date; preserve that history and record the next effective change."]);
    await this.db.execute("INSERT INTO financing_rate_changes (id,facility_id,effective_date,rate_pct_x100,created_at) VALUES (?,?,?,?,?)",[generateId(),facilityId,date,ratePctX100,new Date().toISOString()]);
  }

  async listActivitiesForFacility(facilityId: string): Promise<LoanActivity[]> {
    return (await this.db.select<Record<string,unknown>>("SELECT * FROM loan_activities WHERE facility_id = ? ORDER BY date,created_at,id",[facilityId])).map(mapActivity);
  }

  async listLoanActivities(projectId: string | null): Promise<LoanActivity[]> {
    return (await this.db.select<Record<string,unknown>>(`SELECT a.* FROM loan_activities a JOIN financing_facilities f ON f.id = a.facility_id WHERE ${projectId === null ? "f.project_id IS NULL" : "f.project_id = ?"} ORDER BY a.date,a.created_at,a.id`,projectId === null ? [] : [projectId])).map(mapActivity);
  }

  async recordLoanActivity(facilityId: string, input: LoanActivityInput): Promise<LoanActivity> {
    return this.db.transaction(async()=>{
      this.validDate(input.date);
      if(input.dueDate) this.validDate(input.dueDate);
      if(input.feeTarget && !["origination","draw","other"].includes(input.feeTarget)) throw new ValidationError(["Choose the fee source explicitly."]);
      if(input.kind === "reversal") throw new ValidationError(["Use the explicit reversal operation."]);
      if(!Number.isSafeInteger(input.amountCents)||input.amountCents<=0) throw new ValidationError(["Record a positive exact amount."]);
      const facility=await this.getFacility(facilityId);if(!facility||facility.archivedAt)throw new ValidationError(["Choose an active loan."]);
      if(input.feeTarget === "draw" && (!input.drawId || (await this.getDraw(input.drawId))?.facilityId !== facilityId)) throw new ValidationError(["Choose a draw from this facility for its fee."]);
      const principal=input.principalCents ?? (input.kind === "principal-payment" || input.kind === "payoff" ? input.amountCents : 0);
      const interest=input.interestCents ?? (input.kind === "interest-payment" ? input.amountCents : 0), fee=input.feeCents ?? (input.kind === "fee-payment" ? input.amountCents : 0);
      if([principal,interest,fee].some(n=>!Number.isSafeInteger(n)||n<0)||principal+interest+fee!==input.amountCents)throw new ValidationError(["Principal, interest and fees must reconcile to the payment amount."]);
      if(principal>await this.outstandingPrincipal(facilityId,input.date))throw new ValidationError(["Principal payment exceeds funded outstanding principal on that date."]);
      if(input.expenseId){
        const expense=(await this.db.select<{project_id:string;amount_cents:number;tax_cents:number;voided_at:string|null;bucket:string;date:string}>("SELECT e.project_id,e.amount_cents,e.tax_cents,e.voided_at,e.date,COALESCE(e.bucket_override,c.default_bucket) AS bucket FROM actual_expenses e JOIN expense_categories c ON c.id=e.category_id WHERE e.id = ?",[input.expenseId]))[0];
        if(!expense || expense.voided_at || expense.bucket!=="financing" || expense.date!==input.date || expense.project_id!==facility.projectId || Number(expense.amount_cents)+Number(expense.tax_cents)!==interest+fee)throw new ValidationError(["Match a non-voided financing expense from this project on the payment date for the interest/fee amount only; principal is not an expense."]);
      }
      let expenseId=input.expenseId??null;
      if(!expenseId && facility.projectId && interest+fee>0) {
        const expense=await new ExpenseRepository(this.db).create(facility.projectId,{date:input.date,description:`Loan payment — ${facility.name}`,vendor:facility.lender,categoryId:interest>0?"interest":"financing-fees",amountCents:interest+fee,taxCents:0,bucketOverride:"financing",linkedProposedLineId:null,notes:input.notes??""});
        expenseId=expense.id;
      }
      const row:LoanActivity={drawId:input.drawId??null,dueDate:input.dueDate??null,feeTarget:input.feeTarget??"other",id:generateId(),facilityId,date:input.date,kind:input.kind,amountCents:input.amountCents,principalCents:principal,interestCents:interest,feeCents:fee,expenseId,reversalOfId:null,notes:input.notes??"",createdAt:new Date().toISOString()};
      await this.insertActivity(row);return row;
    });
  }

  private async insertActivity(a:LoanActivity):Promise<void>{
    await this.db.execute("INSERT INTO loan_activities (id,facility_id,date,kind,amount_cents,principal_cents,interest_cents,fee_cents,fee_target,due_date,draw_id,expense_id,reversal_of_id,notes,created_at) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)",[a.id,a.facilityId,a.date,a.kind,a.amountCents,a.principalCents,a.interestCents,a.feeCents,a.feeTarget??"other",a.dueDate??null,a.drawId??null,a.expenseId,a.reversalOfId,a.notes,a.createdAt]);
  }

  async reverseLoanActivity(id:string,date:string,reason:string):Promise<LoanActivity>{
    this.validDate(date);if(!reason.trim())throw new ValidationError(["State why this activity is being reversed."]);
    return this.db.transaction(async()=>{
      const raw=(await this.db.select<Record<string,unknown>>("SELECT * FROM loan_activities WHERE id = ?",[id]))[0];if(!raw)throw new ValidationError(["That activity no longer exists."]);
      const old=mapActivity(raw);if(old.kind === "reversal" || date<old.date)throw new ValidationError(["Reverse an original activity on or after its recorded date."]);
      if(old.expenseId && !(await this.db.select<{voided_at:string|null}>("SELECT voided_at FROM actual_expenses WHERE id = ?",[old.expenseId]))[0]?.voided_at)throw new ValidationError(["Void the linked accounting expense first; its accounting history must also reconcile."]);
      const reversed:LoanActivity={...old,id:generateId(),date,kind:"reversal",amountCents:-old.amountCents,principalCents:-old.principalCents,interestCents:-old.interestCents,feeCents:-old.feeCents,expenseId:null,reversalOfId:old.id,notes:reason,createdAt:new Date().toISOString()};
      await this.insertActivity(reversed);return reversed;
    });
  }

  async setFacilityAllocation(facilityId:string,projectId:string,amountCents:number):Promise<void>{
    if(!Number.isSafeInteger(amountCents)||amountCents<0)throw new ValidationError(["Allocation must be nonnegative integer cents."]);
    const f=await this.getFacility(facilityId);if(!f||f.archivedAt||f.projectId!==null)throw new ValidationError(["Only a business facility can be allocated across projects."]);
    const other=(await this.db.select<{n:number}>("SELECT COALESCE(SUM(amount_cents),0) AS n FROM financing_facility_allocations WHERE facility_id = ? AND project_id <> ?",[facilityId,projectId]))[0].n;
    if(f.commitmentCents!==null && Number(other)+amountCents>f.commitmentCents)throw new ValidationError(["Allocations exceed the facility limit."]);
    const now=new Date().toISOString();await this.db.execute("INSERT INTO financing_facility_allocations (id,facility_id,project_id,amount_cents,created_at,updated_at) VALUES (?,?,?,?,?,?) ON CONFLICT(facility_id,project_id) DO UPDATE SET amount_cents = excluded.amount_cents, updated_at = excluded.updated_at",[generateId(),facilityId,projectId,amountCents,now,now]);
  }

  /* ---------------- payment plans ---------------- */

  async listPaymentPlans(projectId: string): Promise<PaymentPlan[]> {
    const rows = await this.db.select<PaymentPlanRow>(
      "SELECT * FROM payment_plans WHERE project_id = ? AND archived_at IS NULL ORDER BY created_at",
      [projectId],
    );
    return rows.map(mapPaymentPlan);
  }

  async createPaymentPlan(
    projectId: string,
    fields: Omit<PaymentPlan, "id" | "projectId" | "archivedAt" | "createdAt" | "updatedAt">,
  ): Promise<PaymentPlan> {
    const id = generateId();
    const now = new Date().toISOString();
    await this.db.execute(
      `INSERT INTO payment_plans (id, project_id, label, direction, source_kind, source_id, pct_x100,
         amount_cents, anchor, task_id, date, offset_days, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id, projectId, fields.label, fields.direction, fields.sourceKind, fields.sourceId, fields.pctX100,
        fields.amountCents, fields.anchor, fields.taskId, fields.date, fields.offsetDays, fields.notes, now, now,
      ],
    );
    const rows = await this.db.select<PaymentPlanRow>("SELECT * FROM payment_plans WHERE id = ?", [id]);
    return mapPaymentPlan(rows[0]);
  }

  async archivePaymentPlan(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute("UPDATE payment_plans SET archived_at = ?, updated_at = ? WHERE id = ?", [now, now, id]);
  }

  /* ---------------- rental assumptions ---------------- */

  async getRental(projectId: string): Promise<RentalAssumptions | null> {
    const rows = await this.db.select<RentalRow>("SELECT * FROM rental_assumptions WHERE project_id = ?", [
      projectId,
    ]);
    return rows.length === 0 ? null : mapRental(rows[0]);
  }

  async saveRental(
    projectId: string,
    fields: Omit<RentalAssumptions, "projectId" | "updatedAt">,
  ): Promise<RentalAssumptions> {
    const now = new Date().toISOString();
    const params = [
      fields.monthlyRentCents, fields.vacancyPctX100, fields.propertyTaxAnnualCents,
      fields.insuranceAnnualCents, fields.hoaMonthlyCents, fields.managementPctX100,
      fields.maintenancePctX100, fields.utilitiesMonthlyCents, fields.otherMonthlyCents,
      fields.leaseUpDays, now,
    ];
    const existing = await this.getRental(projectId);
    if (existing === null) {
      await this.db.execute(
        `INSERT INTO rental_assumptions (monthly_rent_cents, vacancy_pct_x100, property_tax_annual_cents,
           insurance_annual_cents, hoa_monthly_cents, management_pct_x100, maintenance_pct_x100,
           utilities_monthly_cents, other_monthly_cents, lease_up_days, updated_at, project_id)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [...params, projectId],
      );
    } else {
      await this.db.execute(
        `UPDATE rental_assumptions SET monthly_rent_cents = ?, vacancy_pct_x100 = ?,
           property_tax_annual_cents = ?, insurance_annual_cents = ?, hoa_monthly_cents = ?,
           management_pct_x100 = ?, maintenance_pct_x100 = ?, utilities_monthly_cents = ?,
           other_monthly_cents = ?, lease_up_days = ?, updated_at = ? WHERE project_id = ?`,
        [...params, projectId],
      );
    }
    return (await this.getRental(projectId))!;
  }

  /* ---------------- variance notes ---------------- */

  async listVarianceNotes(projectId: string): Promise<VarianceNote[]> {
    const rows = await this.db.select<{
      id: string; project_id: string; subject_kind: string; subject_id: string; note: string; created_at: string;
    }>("SELECT * FROM variance_notes WHERE project_id = ? ORDER BY created_at", [projectId]);
    return rows.map((r) => ({
      id: r.id, projectId: r.project_id, subjectKind: r.subject_kind, subjectId: r.subject_id,
      note: r.note, createdAt: r.created_at,
    }));
  }

  async addVarianceNote(projectId: string, subjectKind: string, subjectId: string, note: string): Promise<void> {
    await this.db.execute(
      "INSERT INTO variance_notes (id, project_id, subject_kind, subject_id, note, created_at) VALUES (?, ?, ?, ?, ?, ?)",
      [generateId(), projectId, subjectKind, subjectId, note, new Date().toISOString()],
    );
  }
}
